import { measureNaturalWidth, prepareWithSegments } from '@chenglou/pretext'
import { pipe } from '@helo/utils'
import { scaleBand, scaleLinear, scalePoint } from '@visx/scale'

import { Options } from '../../Options'
import { RenderPipeline } from '../../RenderPipeline'
import { ChartAxisConstants } from '../ChartAxis/ChartAxis.constants'
import type { AxisTickLabelOrientation } from '../ChartAxis/ChartAxis.options'
import type {
  ChartBandPack,
  ChartCategoryScaleKind,
  ChartPlotOrientation,
} from '../ChartCartesian'
import type { ChartWrapperPadding, ChartWrapperSlice } from './ChartWrapper'
import { ChartWrapperConstants } from './ChartWrapper.constants'
import { chartWrapperOptions } from './ChartWrapper.options'

export type ResolveWrapperPaddingInput = {
  showXAxisTicks?: boolean
  showYAxisTicks?: boolean
  xDomain?: string[]
  yTicks?: number[]
  xAxisLabel?: string
  yAxisLabel?: string
  xTickLineCount?: number
  xTickLabelOrientation?: AxisTickLabelOrientation
  yTickLabelOrientation?: AxisTickLabelOrientation
  xTickLabels?: string[]
  yTickLabels?: string[]
  plotOrientation?: ChartPlotOrientation
}

const measureTextWidth = (text: string, font: string): number => {
  if (text === '') return 0
  const fallback = text.length * 6
  // ponytail: pretext canvas measure, no DOM reflow; falls back if canvas unavailable (tests)
  try {
    const width = measureNaturalWidth(prepareWithSegments(text, font))
    return Number.isFinite(width) && width >= 0 ? width : fallback
  } catch {
    return fallback
  }
}

const rotatedLabelExtent = (
  labels: string[],
  orientation: AxisTickLabelOrientation,
): { alongAxis: number; awayFromAxis: number } => {
  const maxWidth = labels.reduce((maxWidth, label) => {
    const width = measureTextWidth(label, ChartWrapperConstants.tickFont)
    return width > maxWidth ? width : maxWidth
  }, 0)
  if (orientation === 'diagonal') {
    const hyp = Math.SQRT1_2
    return {
      alongAxis: maxWidth * hyp,
      awayFromAxis: maxWidth * hyp + ChartWrapperConstants.tickFontSize * hyp,
    }
  }
  if (orientation === 'vertical') {
    return {
      alongAxis: ChartWrapperConstants.tickFontSize,
      awayFromAxis: maxWidth,
    }
  }
  return {
    alongAxis: maxWidth,
    awayFromAxis: ChartWrapperConstants.tickFontSize,
  }
}

const resolvePadding = ({
  showXAxisTicks,
  showYAxisTicks,
  xDomain,
  yTicks,
  xAxisLabel,
  yAxisLabel,
  xTickLineCount = 1,
  xTickLabelOrientation = 'horizontal',
  yTickLabelOrientation = 'horizontal',
  xTickLabels,
  yTickLabels,
  plotOrientation = 'vertical',
}: ResolveWrapperPaddingInput): ChartWrapperPadding => {
  let left = ChartWrapperConstants.minLeft
  let bottom = ChartWrapperConstants.minBottom
  let right = ChartWrapperConstants.rightPadding

  const leftTickLabels =
    yTickLabels ??
    (plotOrientation === 'horizontal'
      ? (xDomain ?? [])
      : (yTicks ?? []).map(tick => String(tick)))
  if (yTickLabelOrientation === 'horizontal') {
    const tickWidth = leftTickLabels.reduce((maxWidth, label) => {
      const width = measureTextWidth(label, ChartWrapperConstants.tickFont)
      return width > maxWidth ? width : maxWidth
    }, 0)
    left = Math.ceil(
      tickWidth +
        ChartWrapperConstants.yTickMargin +
        ChartWrapperConstants.yTickLabelOverflowPad,
    )
  } else {
    const extent = rotatedLabelExtent(leftTickLabels, yTickLabelOrientation)
    left = Math.ceil(
      extent.awayFromAxis +
        ChartWrapperConstants.yTickMargin +
        ChartWrapperConstants.yTickLabelOverflowPad,
    )
  }
  if (yAxisLabel) {
    // Rotated Y label: horizontal cost is roughly the font size, not string width.
    left +=
      ChartWrapperConstants.axisLabelFontSize + ChartWrapperConstants.yLabelGap
  }
  if (showYAxisTicks) {
    left += ChartAxisConstants.axisTickLength
  }

  const bottomTickLabels =
    xTickLabels ??
    (plotOrientation === 'horizontal'
      ? (yTicks ?? []).map(tick => String(tick))
      : (xDomain ?? []))
  if (xTickLabelOrientation === 'horizontal') {
    bottom =
      ChartWrapperConstants.xTickGap +
      ChartWrapperConstants.tickFontSize * Math.max(1, xTickLineCount)
  } else {
    const extent = rotatedLabelExtent(bottomTickLabels, xTickLabelOrientation)
    bottom = ChartWrapperConstants.xTickGap + Math.ceil(extent.awayFromAxis)
    right = Math.max(right, Math.ceil(ChartWrapperConstants.tickFontSize))
  }
  if (xAxisLabel) {
    // VisX bottom-label y = labelOffset + tickFont + titleFont (baseline).
    // Add descender pad so glyphs like "y" aren't clipped by the SVG edge.
    bottom +=
      ChartWrapperConstants.xLabelGap +
      ChartWrapperConstants.axisLabelFontSize +
      ChartWrapperConstants.axisLabelDescenderPad
  }
  if (showXAxisTicks) {
    bottom += ChartAxisConstants.axisTickLength
  }

  return {
    top: ChartWrapperConstants.topPadding,
    right,
    left: Math.max(ChartWrapperConstants.minLeft, left),
    bottom: Math.max(ChartWrapperConstants.minBottom, bottom),
  }
}

const resolveTotalHeight = (
  chartHeight: number,
  padding: ChartWrapperPadding,
): number => {
  const plotHeight =
    chartHeight -
    ChartWrapperConstants.baselinePadding.top -
    ChartWrapperConstants.baselinePadding.bottom
  return plotHeight + padding.top + padding.bottom
}

const autoFillBandPadding = ({
  categoryCount,
  bandSize,
  available,
}: {
  categoryCount: number
  bandSize: number
  available: number
}): { paddingInner: number; paddingOuter: number } => {
  if (
    categoryCount <= 1 ||
    bandSize <= 0 ||
    available <= categoryCount * bandSize
  ) {
    return { paddingInner: 0, paddingOuter: 0 }
  }
  const gap = (available - categoryCount * bandSize) / (categoryCount - 1)
  const step = bandSize + gap
  return { paddingInner: gap / step, paddingOuter: 0 }
}

const packedCategoryRange = ({
  available,
  packedLength,
}: {
  available: number
  packedLength: number
}): [number, number] => {
  const offset = Math.max(0, (available - packedLength) / 2)
  return [offset, offset + packedLength]
}

const buildScales = ({
  xDomain,
  yDomain,
  innerWidth,
  innerHeight,
  plotOrientation = 'vertical',
  categoryScale = 'point',
  bandPack,
}: {
  xDomain: string[]
  yDomain: [number, number]
  innerWidth: number
  innerHeight: number
  plotOrientation?: ChartPlotOrientation
  categoryScale?: ChartCategoryScaleKind
  bandPack?: ChartBandPack
}) => {
  const isHorizontal = plotOrientation === 'horizontal'
  const useBand = categoryScale === 'band'
  const available = isHorizontal ? innerHeight : innerWidth
  const packedLength = bandPack?.packedLength
  const bandSize = bandPack?.bandSize
  const autoPadding =
    useBand &&
    (packedLength == null || packedLength <= 0) &&
    bandSize != null &&
    bandSize > 0
      ? autoFillBandPadding({
          categoryCount: xDomain.length,
          bandSize,
          available,
        })
      : null
  const fillAvailable = autoPadding != null
  const categoryRange: [number, number] =
    !fillAvailable &&
    packedLength != null &&
    packedLength > 0 &&
    packedLength <= available
      ? packedCategoryRange({ available, packedLength })
      : [0, available]
  const paddingInner =
    autoPadding?.paddingInner ??
    bandPack?.paddingInner ??
    (isHorizontal
      ? ChartWrapperConstants.horizontalBandPaddingInner
      : ChartWrapperConstants.verticalBandPaddingInner)
  const paddingOuter =
    autoPadding?.paddingOuter ??
    bandPack?.paddingOuter ??
    (isHorizontal
      ? ChartWrapperConstants.horizontalBandPaddingOuter
      : ChartWrapperConstants.verticalBandPaddingOuter)

  const category = useBand
    ? scaleBand<string>({
        domain: xDomain,
        range: categoryRange,
        paddingInner,
        paddingOuter,
      })
    : scalePoint<string>({
        domain: xDomain,
        range: isHorizontal ? [0, innerHeight] : [0, innerWidth],
        padding: 0,
      })
  const value = scaleLinear<number>({
    domain: yDomain,
    range: isHorizontal ? [0, innerWidth] : [innerHeight, 0],
  })

  return isHorizontal
    ? {
        orientation: 'horizontal' as const,
        xScale: value,
        yScale: category,
      }
    : {
        orientation: 'vertical' as const,
        xScale: category,
        yScale: value,
      }
}

/** Seed wrapper height and baseline padding. */
const setWrapper = () =>
  RenderPipeline.updateOutput(
    (
      _output: { wrapper?: ChartWrapperSlice },
      ctx: { input: { height?: number } },
    ) => ({
      wrapper: {
        chartHeight: ctx.input.height,
        padding: ChartWrapperConstants.baselinePadding,
      },
    }),
  )

/** Resolve wrapper height from defaults. */
const setupInput = () =>
  RenderPipeline.updateInput(input =>
    pipe(
      input,
      Options.applyDefaults(chartWrapperOptions),
      Options.validate(chartWrapperOptions),
    ),
  )

export const ChartWrapperInputParts = {
  setupInput,
}

export const ChartWrapperOutputParts = {
  setWrapper,
}

export const ChartWrapperUtils = {
  measureTextWidth,
  resolvePadding,
  resolveTotalHeight,
  autoFillBandPadding,
  packedCategoryRange,
  buildScales,
}
